import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchRecommendations } from '../services/recommendations';
import { useBookDetails } from '../hooks/useBookDetails';
import { useBookRating } from '../hooks/useBookRating';
import { useFormattedDescription } from '../hooks/useFormattedDescription';
import { ALL_TAGS } from '../data/seedTags';
import FlameVideo from './FlameVideo';
import './DiscoverView.css';

const SWIPE_THRESHOLD = 100;


export default function DiscoverView({ searchParams, myList, setMyList, shownBooks, addShownBooks, onNavigateToList, onBackToSearch }) {
  const [books, setBooks] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeVibes, setActiveVibes] = useState(searchParams?.vibes ?? []);
  const [showFilter, setShowFilter] = useState(false);
  const [history, setHistory] = useState([]);
  const [swipeDir, setSwipeDir] = useState(null);
  const [cardKey, setCardKey] = useState(0);

  const dragRef = useRef({ active: false, startX: 0, dx: 0 });
  const coverRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'ArrowRight') flyOut('right');
      if (e.key === 'ArrowLeft')  flyOut('left');
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [books, index]);

  function seenKey(book) {
    return `"${book.title}" by ${book.author}`;
  }

  async function load(vibes) {
    setLoading(true);
    setError(null);
    try {
      const results = await fetchRecommendations({
        anchorBooks:   searchParams?.anchorBooks ?? [],
        selectedVibes: vibes ?? activeVibes,
        category:      searchParams?.category   ?? null,
        shownBooks,
      });
      // Record every book in this batch session-wide so it's never repeated,
      // even if the user never swipes it
      addShownBooks(results.map(seenKey));
      setBooks(results);
      setIndex(0);
      setHistory([]);
    } catch (e) {
      console.error('[Matchbook] load() error:', e);
      setError("Couldn't load recommendations. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  const currentBook = books[index];

  const commitSwipe = useCallback((dir) => {
    if (!books[index]) return;
    if (dir === 'right') setMyList(prev => [...prev, books[index]]);
    setHistory(prev => [...prev, { book: books[index], dir }]);
    setSwipeDir(dir);
    setTimeout(() => {
      setIndex(i => i + 1);
      setSwipeDir(null);
      setCardKey(k => k + 1);
      if (coverRef.current) {
        coverRef.current.style.transition = 'none';
        coverRef.current.style.transform = 'none';
        coverRef.current.style.opacity = '1';
      }
    }, 400);
  }, [books, index, setMyList]);

  function handleUndo() {
    if (!history.length) return;
    const last = history[history.length - 1];
    if (last.dir === 'right') setMyList(prev => prev.filter(b => b.key !== last.book.key));
    setHistory(prev => prev.slice(0, -1));
    setIndex(i => i - 1);
    setCardKey(k => k + 1);
  }

  function onDragStart(e) {
    if (swipeDir) return;
    dragRef.current = { active: true, startX: (e.touches ? e.touches[0] : e).clientX, dx: 0 };
    if (coverRef.current) coverRef.current.style.transition = 'none';
  }

  function onDragMove(e) {
    if (!dragRef.current.active) return;
    const dx = (e.touches ? e.touches[0] : e).clientX - dragRef.current.startX;
    dragRef.current.dx = dx;
    if (coverRef.current) {
      coverRef.current.style.transform = `translate(${dx}px, ${-Math.abs(dx) * 0.04}px) rotate(${dx / 22}deg)`;
    }
    setSwipeDir(Math.abs(dx) > 40 ? (dx > 0 ? 'right' : 'left') : null);
  }

  function onDragEnd() {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    const dx = dragRef.current.dx;
    if (coverRef.current) coverRef.current.style.transition = 'transform 0.28s ease';
    if (dx > SWIPE_THRESHOLD)       flyOut('right');
    else if (dx < -SWIPE_THRESHOLD) flyOut('left');
    else {
      if (coverRef.current) coverRef.current.style.transform = 'none';
      setSwipeDir(null);
    }
  }

  function flyOut(dir) {
    if (!books[index]) return;
    if (coverRef.current) {
      const flyX = dir === 'right' ? 700 : -700;
      coverRef.current.style.transition = 'transform 0.38s cubic-bezier(0.4,0,0.6,1), opacity 0.28s ease 0.1s';
      coverRef.current.style.transform = `translate(${flyX}px, -20px) rotate(${dir === 'right' ? 24 : -24}deg)`;
      coverRef.current.style.opacity = '0';
    }
    commitSwipe(dir);
  }

  if (loading) return <LoadingState />;
  if (error)   return <ErrorState message={error} onRetry={() => load()} />;

  return (
    <div className="discover-view">
      <header className="discover-topbar">
        <button className="discover-wordmark discover-wordmark--btn" onClick={onBackToSearch} aria-label="New search">
          <FlameVideo height={32} />
          <img src="/matchbook-logo.svg" alt="Matchbook" className="wordmark-logo" />
        </button>
        <div className="discover-topbar-actions">
          <button className="discover-iconbtn" onClick={() => setShowFilter(true)} aria-label="Filters">
            <i className="ph ph-sliders" />
          </button>
          <button className="discover-mylist-btn" onClick={onNavigateToList}>
            <i className="ph ph-bookmarks" />
            My List
            {myList.length > 0 && <span className="mylist-badge">{myList.length}</span>}
          </button>
        </div>
      </header>

      <main className="discover-main">
        {!currentBook ? (
          <EmptyDeck onReset={() => load()} />
        ) : (
          <div className="discover-layout" key={cardKey}>
            {/* Cover column */}
            <div className="discover-cover-col">
              <div
                className="discover-cover-wrap"
                ref={coverRef}
                onMouseDown={onDragStart}
                onMouseMove={onDragMove}
                onMouseUp={onDragEnd}
                onMouseLeave={onDragEnd}
                onTouchStart={onDragStart}
                onTouchMove={onDragMove}
                onTouchEnd={onDragEnd}
              >
                {books[index + 2] && <div className="cover-stack cover-stack-3" />}
                {books[index + 1] && <div className="cover-stack cover-stack-2" />}

                {currentBook.coverUrl ? (
                  <img
                    className="discover-cover-img"
                    src={currentBook.coverUrl.replace('-M.jpg', '-L.jpg')}
                    alt={currentBook.title}
                    draggable={false}
                  />
                ) : (
                  <div className="discover-cover-fallback">
                    <span>{currentBook.title}</span>
                  </div>
                )}

                <div className={`swipe-stamp stamp-save${swipeDir === 'right' ? ' stamp-visible' : ''}`}>
                  <i className="ph-fill ph-heart" />
                </div>
                <div className={`swipe-stamp stamp-pass${swipeDir === 'left' ? ' stamp-visible' : ''}`}>
                  <i className="ph ph-x-circle" />
                </div>

              </div>
              <p className="discover-progress">{index + 1} / {books.length}</p>
            </div>

            {/* Info panel */}
            <InfoPanel
              book={currentBook}
              selectedVibes={activeVibes}
              onPass={() => flyOut('left')}
              onSave={() => flyOut('right')}
              onUndo={handleUndo}
              canUndo={history.length > 0}
            />
          </div>
        )}
      </main>

      {showFilter && (
        <FilterModal
          activeVibes={activeVibes}
          onApply={(vibes) => {
            setActiveVibes(vibes);
            load(vibes);
            setShowFilter(false);
          }}
          onClose={() => setShowFilter(false)}
        />
      )}
    </div>
  );
}

/* ── Info panel ── */
const MIN_RATINGS = 25;

function InfoPanel({ book, selectedVibes, onPass, onSave, onUndo, canUndo }) {
  const { details } = useBookDetails(book);
  const hcRating = useBookRating(book);
  const rawDescription = book.description || details?.description || null;
  // Pass the RESOLVED description (which may have come from details / Open Library,
  // not book.description) so it actually gets formatted and rendered.
  const bookForFmt = rawDescription ? { ...book, description: rawDescription } : null;
  const { formatted: formattedDescription, loading: descLoading } = useFormattedDescription(bookForFmt);
  const description = formattedDescription ?? rawDescription;

  const showRating = hcRating?.ratingsCount >= MIN_RATINGS;

  return (
    <div className="discover-info-col">
      <div className="discover-info-panel">

        {/* Title row — tags (category + vibes) in upper right */}
        <div className="discover-header-row">
          <div className="discover-title-block">
            <h2 className="discover-title">{book.title}</h2>
            <p className="discover-author">{book.author}</p>
          </div>
          <div className="discover-header-tags">
            {book.bookCategory && (
              <span className="match-tag match-tag--category">{book.bookCategory}</span>
            )}
            {selectedVibes?.map(v => (
              <span key={v} className="match-tag match-tag--vibe">{v}</span>
            ))}
          </div>
        </div>

        <div className="discover-meta-row">
          {book.year && <span>{book.year}</span>}
          {details?.pageCount && <span>{details.pageCount} pages</span>}
          {showRating && (
            hcRating.slug ? (
              <a
                className="meta-rating meta-rating--link"
                href={`https://hardcover.app/books/${hcRating.slug}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                ★ {hcRating.rating.toFixed(1)}
                <span className="meta-rating__count">{hcRating.ratingsCount.toLocaleString()} ratings</span>
              </a>
            ) : (
              <span className="meta-rating">
                ★ {hcRating.rating.toFixed(1)}
                <span className="meta-rating__count">{hcRating.ratingsCount.toLocaleString()} ratings</span>
              </span>
            )
          )}
        </div>

        {/* Actions below meta row */}
        <div className="discover-actions">
          <button className="action-btn action-btn--undo" onClick={onUndo} disabled={!canUndo} aria-label="Undo" title="Undo last swipe">
            <i className="ph ph-arrow-u-up-left" />
          </button>
          <button className="action-btn action-btn--pass" onClick={onPass} aria-label="Skip">
            <i className="ph ph-x-circle" />
            <span>Skip</span>
          </button>
          <button className="action-btn action-btn--save" onClick={onSave} aria-label="Save to list">
            <i className="ph-fill ph-heart" />
            <span>Save</span>
          </button>
        </div>

        {book.matchReason && (
          <p className="discover-match-reason">{book.matchReason}</p>
        )}

        {rawDescription && descLoading && <DescriptionSkeleton />}
        {description && <DescriptionBlock description={description} />}

        <div className="discover-links">
          <a href={`https://bookshop.org/search?keywords=${encodeURIComponent(book.title + ' ' + book.author)}`}
             target="_blank" rel="noopener noreferrer" className="discover-buy-link">
            <i className="ph ph-storefront" /> Bookshop.org
          </a>
          <a href={`https://www.thriftbooks.com/browse/?b.search=${encodeURIComponent(book.title + ' ' + book.author)}`}
             target="_blank" rel="noopener noreferrer" className="discover-buy-link">
            <i className="ph ph-books" /> ThriftBooks
          </a>
          <a href={`https://search.worldcat.org/search?q=${encodeURIComponent(book.title + ' ' + book.author)}`}
             target="_blank" rel="noopener noreferrer" className="discover-buy-link">
            <i className="ph ph-library" /> Find in a library
          </a>
        </div>
      </div>
    </div>
  );
}

/* ── Description skeleton ── */
function DescriptionSkeleton() {
  return (
    <div className="desc-skeleton">
      <div className="desc-skeleton__line" style={{ width: '100%' }} />
      <div className="desc-skeleton__line" style={{ width: '92%' }} />
      <div className="desc-skeleton__line" style={{ width: '97%' }} />
      <div className="desc-skeleton__line" style={{ width: '85%' }} />
      <div className="desc-skeleton__line" style={{ width: '60%' }} />
    </div>
  );
}

/* ── Description ── */
function DescriptionBlock({ description }) {
  const paragraphs = description.split(/\n+/).map(p => p.trim()).filter(Boolean);

  return (
    <div className="discover-description">
      {paragraphs.map((para, i) => <p key={i}>{para}</p>)}
    </div>
  );
}

/* ── Filter modal ── */
function FilterModal({ activeVibes, onApply, onClose }) {
  const [localVibes, setLocalVibes] = useState(activeVibes);

  function toggleVibe(v) {
    setLocalVibes(prev =>
      prev.includes(v) ? prev.filter(t => t !== v)
      : prev.length < 3 ? [...prev, v]
      : prev
    );
  }

  return (
    <div className="filter-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="filter-modal">
        <div className="filter-modal__header">
          <span className="filter-modal__title">Refine vibes</span>
          <button className="discover-iconbtn" onClick={onClose}><i className="ph ph-x" /></button>
        </div>

        <div className="filter-modal__body">
          <div className="filter-section">
            <p className="filter-label">
              Vibes
              {localVibes.length > 0 && <span className="filter-vibe-count">{localVibes.length}/3</span>}
            </p>
            <div className="filter-vibe-grid">
              {ALL_TAGS.map(tag => {
                const selected = localVibes.includes(tag);
                const disabled = !selected && localVibes.length >= 3;
                return (
                  <button
                    key={tag}
                    className={`filter-vibe-pill${selected ? ' filter-vibe-pill--selected' : ''}${disabled ? ' filter-vibe-pill--disabled' : ''}`}
                    onClick={() => toggleVibe(tag)}
                    disabled={disabled}
                  >
                    {selected && <i className="ph ph-check" />}
                    {tag}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="filter-modal__footer">
          <button className="filter-cta" onClick={() => onApply(localVibes)}>
            <i className="ph ph-sparkle" /> Refresh results
          </button>
        </div>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="discover-view discover-state-screen">
      <video
        className="loading-video"
        src="/sprite-witch.mp4"
        autoPlay
        loop
        muted
        playsInline
      />
      <p className="loading-text">Finding your next great read</p>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="discover-view discover-state-screen">
      <p className="state-text">{message}</p>
      <button className="ghost-btn" onClick={onRetry}>Try again</button>
    </div>
  );
}

function EmptyDeck({ onReset }) {
  return (
    <div className="empty-deck">
      <div className="empty-deck__icon">📚</div>
      <h3>That's everything!</h3>
      <p>You've seen all the recommendations for this search.</p>
      <button className="ghost-btn" onClick={onReset}>Start over</button>
    </div>
  );
}
